import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

const DEFAULT_FORMATS = ["qr_code", "ean_13", "ean_8", "upc_a", "upc_e", "code_128"];

export interface CodeScannerProps {
  active: boolean;
  onDetected: (raw: string) => void;
  formats?: string[];
  className?: string;
}

type StopDecode = () => void;

function barcodeDetectorCtor(): typeof BarcodeDetector | null {
  if (typeof window === "undefined") return null;
  return typeof window.BarcodeDetector === "function" ? window.BarcodeDetector : null;
}

async function startBarcodeDetector(
  video: HTMLVideoElement,
  formats: string[],
  onCode: (raw: string) => void,
  isStopped: () => boolean,
): Promise<StopDecode | null> {
  const Ctor = barcodeDetectorCtor();
  if (!Ctor) return null;
  try {
    if (typeof Ctor.getSupportedFormats === "function") {
      const supported = await Ctor.getSupportedFormats();
      if (Array.isArray(supported) && supported.length > 0 && !supported.includes("qr_code")) {
        return null;
      }
    }
    const detector = new Ctor({ formats });
    let rafId = 0;
    const tick = async () => {
      if (isStopped()) return;
      try {
        const codes = await detector.detect(video);
        const raw = String(codes[0]?.rawValue || "").trim();
        if (raw) onCode(raw);
      } catch {
        // transiente Detektorfehler ignorieren
      }
      if (!isStopped()) {
        rafId = window.requestAnimationFrame(() => {
          void tick();
        });
      }
    };
    rafId = window.requestAnimationFrame(() => {
      void tick();
    });
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  } catch {
    return null;
  }
}

async function startZxingDecoder(
  stream: MediaStream,
  video: HTMLVideoElement,
  onCode: (raw: string) => void,
  isStopped: () => boolean,
): Promise<StopDecode> {
  const { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType } = await import("@zxing/library");
  if (isStopped()) return () => {};
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
  ]);
  const reader = new BrowserMultiFormatReader(hints, 350);
  await reader.decodeFromStream(stream, video, (result) => {
    if (isStopped()) return;
    const raw = result?.getText()?.trim();
    if (raw) onCode(raw);
  });
  return () => {
    try {
      reader.reset();
    } catch {
      // bereits gestoppt
    }
  };
}

async function waitForVideoFrame(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return;
  await new Promise<void>((resolve) => {
    const done = () => resolve();
    video.addEventListener("loadeddata", done, { once: true });
    window.setTimeout(done, 1500);
  });
}

export function CodeScanner({
  active,
  onDetected,
  formats = DEFAULT_FORMATS,
  className,
}: CodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const onDetectedRef = useRef(onDetected);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (!active) {
      setCameraError(null);
      setCameraReady(false);
      return;
    }

    const videoEl = videoRef.current;
    if (!videoEl) return;

    videoEl.setAttribute("playsinline", "true");
    videoEl.setAttribute("webkit-playsinline", "true");

    let stream: MediaStream | null = null;
    let stopped = false;
    let stopDecode: StopDecode | null = null;

    async function start(video: HTMLVideoElement) {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("Kamera ist auf diesem Gerät nicht verfügbar.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (stopped) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        video.srcObject = stream;
        await video.play();
        await waitForVideoFrame(video);
        if (stopped) return;
        setCameraReady(true);

        const emit = (raw: string) => onDetectedRef.current(raw);
        const isStopped = () => stopped;

        stopDecode = await startBarcodeDetector(video, formats, emit, isStopped);
        if (!stopDecode) {
          stopDecode = await startZxingDecoder(stream, video, emit, isStopped);
        }
      } catch (err) {
        if (stopped) return;
        const message =
          err instanceof Error && /permission|notallowed|denied/i.test(err.name + err.message)
            ? "Kamera-Zugriff verweigert. In Safari unter Einstellungen → Safari → Kamera erlauben."
            : "Kamera konnte nicht gestartet werden. Zugriff erlauben oder Code unten einfügen.";
        setCameraError(message);
      }
    }

    void start(videoEl);

    return () => {
      stopped = true;
      stopDecode?.();
      videoEl.pause();
      videoEl.srcObject = null;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [active, formats]);

  return (
    <div className={clsx("relative overflow-hidden rounded-ios bg-black", className)}>
      <video
        ref={videoRef}
        className="aspect-[3/4] h-auto w-full object-cover sm:aspect-video"
        playsInline
        muted
        autoPlay
      />
      {cameraReady && !cameraError ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-44 w-44 rounded-ios border-2 border-white/80 shadow-[0_0_0_999px_rgba(0,0,0,0.28)] sm:h-52 sm:w-52" />
        </div>
      ) : null}
      {cameraError ? (
        <div className="absolute inset-x-0 bottom-0 bg-black/70 px-3 py-2 text-center text-xs text-white">
          {cameraError}
        </div>
      ) : null}
    </div>
  );
}
